// framework
import { immerable, produce } from "immer";
// api
import * as Client from "../../../../api/Client";
import { CallResultError } from "../../../../api/callHelper";
import { INopimsBasketItemModel } from "../../nopimsBasketSlice";

export enum ViewStateEnum {
    Basket,
    Download,
    Remove,
    Request,
    Submit,
}

export interface IReferenceDataViewModel {
    readonly honorifics: Client.GetSubmitPublicNopimsBasketReferenceDataResponseHonorificDto[];
    readonly countries: Client.GetSubmitPublicNopimsBasketReferenceDataResponseCountryDto[];
    readonly countrySubdivisions: Client.GetSubmitPublicNopimsBasketReferenceDataResponseCountrySubDivisionDto[];
}

export interface IReferenceDataViewModel {
    readonly honorifics: Client.GetSubmitPublicNopimsBasketReferenceDataResponseHonorificDto[];
    readonly countries: Client.GetSubmitPublicNopimsBasketReferenceDataResponseCountryDto[];
    readonly countrySubdivisions: Client.GetSubmitPublicNopimsBasketReferenceDataResponseCountrySubDivisionDto[];
}

export interface ICustomerDetailsViewModel {
    readonly honorific?: Client.GetSubmitPublicNopimsBasketReferenceDataResponseHonorificDto | undefined;
    readonly firstName?: string | undefined;
    readonly lastName?: string | undefined;
    readonly emailAddress?: string | undefined;
    readonly contactNumber?: string | undefined;
    readonly companyName?: string | undefined;
    readonly address?: string | undefined;
    readonly suburb?: string | undefined;
    readonly postcode?: string | undefined;
    readonly country?: Client.GetSubmitPublicNopimsBasketReferenceDataResponseCountryDto | undefined;
    readonly countrySubdivision?: Client.GetSubmitPublicNopimsBasketReferenceDataResponseCountrySubDivisionDto | undefined;
    readonly comments?: string | undefined;
}

export interface IRootViewModel {
    readonly viewState: ViewStateEnum;
    readonly statusMessages?: Client.StatusMessagesDto;
    readonly isBusy: boolean;
    readonly isDirty: boolean;
    readonly error: CallResultError | undefined;
    readonly customerDetails: ICustomerDetailsViewModel;
    readonly requestedBasketItems: INopimsBasketItemModel[];
    readonly referenceData: IReferenceDataViewModel;

    stateChanged(state: ViewStateEnum): IRootViewModel;
    customerDetailsChanged(values: ICustomerDetailsViewModel): IRootViewModel;
    requestedBasketItemsChanged(values: INopimsBasketItemModel[]): IRootViewModel;
    referenceDataLoading(): IRootViewModel;
    referenceDataLoaded(response: Client.GetSubmitPublicNopimsBasketReferenceDataResponseDto | undefined, error: CallResultError | undefined): IRootViewModel;
    basketSubmitting(): IRootViewModel;
    basketSubmitted(response: Client.SubmitPublicNopimsBasketResponseDto | undefined, error: CallResultError | undefined): IRootViewModel;
    basketExporting(): IRootViewModel;
    basketExported(error: CallResultError | undefined): IRootViewModel;
}

export class RootViewModel implements IRootViewModel {
    [immerable] = true;

    constructor() {
        this.viewState = ViewStateEnum.Basket;
        this.statusMessages = undefined;
        this.isBusy = false;
        this.isDirty = false;
        this.error = undefined;
        this.customerDetails = {};
        this.requestedBasketItems = [];
        this.referenceData = {
            honorifics: [],
            countries: [],
            countrySubdivisions: [],
        };
    }

    public readonly viewState: ViewStateEnum;
    public readonly statusMessages?: Client.StatusMessagesDto;
    public readonly isBusy: boolean;
    public readonly isDirty: boolean;
    public readonly error: CallResultError | undefined;
    public readonly customerDetails: ICustomerDetailsViewModel;
    public readonly requestedBasketItems: INopimsBasketItemModel[];
    public readonly referenceData: IReferenceDataViewModel;

    public stateChanged(state: ViewStateEnum): IRootViewModel {
        return produce(this, (draft) => {
            draft.viewState = state;
            draft.statusMessages = undefined;

            if (state === ViewStateEnum.Download || state === ViewStateEnum.Request || state === ViewStateEnum.Remove) {
                draft.isDirty = true;
            } else {
                draft.isDirty = false;
            }

            if (state === ViewStateEnum.Submit) {
                draft.customerDetails = {
                    country: draft.referenceData.countries.find((c) => c.name === "Australia"),
                };
            }
        });
    }

    public customerDetailsChanged(values: ICustomerDetailsViewModel): IRootViewModel {
        return produce(this, (draft) => {
            draft.customerDetails.honorific = values.honorific;
            draft.customerDetails.firstName = values.firstName;
            draft.customerDetails.lastName = values.lastName;
            draft.customerDetails.emailAddress = values.emailAddress;
            draft.customerDetails.contactNumber = values.contactNumber;
            draft.customerDetails.companyName = values.companyName;
            draft.customerDetails.address = values.address;
            draft.customerDetails.suburb = values.suburb;
            draft.customerDetails.postcode = values.postcode;
            draft.customerDetails.country = values.country;
            draft.customerDetails.countrySubdivision = values.countrySubdivision;
            draft.customerDetails.comments = values.comments;
            draft.isDirty = true;
        });
    }

    public requestedBasketItemsChanged(values: INopimsBasketItemModel[]): IRootViewModel {
        return produce(this, (draft) => {
            draft.requestedBasketItems = values;
        });
    }

    public referenceDataLoading(): IRootViewModel {
        return produce(this, (draft) => {
            draft.isBusy = true;
        });
    }

    public referenceDataLoaded(response: Client.GetSubmitPublicNopimsBasketReferenceDataResponseDto | undefined, error: CallResultError | undefined): IRootViewModel {
        return produce(this, (draft) => {
            if (response) {
                draft.referenceData.honorifics = response.honorifics;
                draft.referenceData.countries = response.countries;
                draft.referenceData.countrySubdivisions = response.countrySubDivisions;
                draft.customerDetails.country = response.countries.find((c) => c.name === "Australia");
                draft.isDirty = false;
            }

            draft.isBusy = false;
            draft.error = error;
        });
    }

    public basketSubmitting(): IRootViewModel {
        return produce(this, (draft) => {
            draft.isBusy = true;
        });
    }

    public basketSubmitted(response: Client.SubmitPublicNopimsBasketResponseDto | undefined, error: CallResultError | undefined) {
        return produce(this, (draft) => {
            if (response) {
                draft.statusMessages = response.statusMessages;
                if (response.statusMessages.isSuccess) {
                    draft.isDirty = false;
                    draft.viewState = ViewStateEnum.Basket;
                    draft.requestedBasketItems = [];
                }
            }

            draft.isBusy = false;
            draft.error = error;
        });
    }

    public basketExporting(): IRootViewModel {
        return produce(this, (draft) => {
            draft.isBusy = true;
        });
    }

    public basketExported(error: CallResultError | undefined) {
        return produce(this, (draft) => {
            draft.isBusy = false;
            draft.error = error;
        });
    }
}
